import { ArrowRight, CameraIcon } from 'lucide-react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  setResumeTitle,
  updatePersonalInfo
} from '../../redux/Slices/ResumeSlice';
import { useAppSelector } from '../../redux/store/hooks';
import {
  useGetFileFromS3Mutation,
  useUploadFileToS3Mutation
} from '../../services/CloudApi';
import Button from '../Common/Button';

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
};

const MAX_CHAR_LIMIT = 15;
const NAME_CHAR_LIMIT = 50;
const TITLE_MAX_CHAR_LIMIT = 100;

const PersonalInfoForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  onNext
}) => {
  const dispatch = useDispatch();
  const personalInfo = useAppSelector(state => state.root.resume.personalInfo);
  const resumeTitle = useAppSelector(state => state.root.resume.title);
  const [resumePic, setResumePic] = useState('');
  const [uploadError, setUploadError] = useState('');

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value.length > TITLE_MAX_CHAR_LIMIT) return;
    dispatch(setResumeTitle(value));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const nameFields = ['firstName', 'lastName', 'additionalName'];
    const otherLimitedFields = ['nationality', 'placeOfBirth'];
    if (nameFields.includes(name) && value.length > NAME_CHAR_LIMIT) return;
    if (otherLimitedFields.includes(name) && value.length > MAX_CHAR_LIMIT)
      return;

    const formattedValue =
      name === 'dateOfBirth' && value ? new Date(value).toISOString() : value;

    const updatedFirstName =
      name === 'firstName' ? formattedValue : personalInfo.firstName;
    const updatedLastName =
      name === 'lastName' ? formattedValue : personalInfo.lastName;

    dispatch(
      updatePersonalInfo({
        [name]: formattedValue,
        fullName: `${updatedFirstName} ${updatedLastName}`.trim()
      })
    );
  };

  const [uploadFile] = useUploadFileToS3Mutation();
  const [getFile] = useGetFileFromS3Mutation();

  const isFormValid =
    personalInfo.firstName?.trim() &&
    personalInfo.lastName?.trim() &&
    resumeTitle?.trim();

  const generateProfileUrl = async (fileName: string): Promise<string> => {
    const url = await getFile(fileName).unwrap();
    return url;
  };

  const handleUploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const fileInput = e.target;
    const files: FileList | null = fileInput.files;
    if (!files) return;

    const file = files[0];
    setUploadError('');

    const allowedTypes = ['image/png', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please upload a PNG or JPEG image only');
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File size must be less than 2MB');
      return;
    }

    try {
      const response = await uploadFile(file).unwrap();
      const fileName = response.data.fileName;
      dispatch(updatePersonalInfo({ profilePhoto: response.data }));
      const url = await generateProfileUrl(fileName);
      setResumePic(url);
    } catch (error) {
      console.error(error);
    }
  };

  const CharCount = ({
    value,
    max = MAX_CHAR_LIMIT
  }: {
    value: string | undefined;
    max?: number;
  }) => {
    const len = value?.length || 0;
    return (
      <span
        className={`text-xs ${len >= max ? 'text-red-500' : 'text-gray-400'}`}
      >
        {len}/{max}
      </span>
    );
  };

  useEffect(() => {
    const fileName = personalInfo.profilePhoto?.fileName;
    if (fileName && !resumePic) {
      generateProfileUrl(fileName).then(url => setResumePic(url));
    }
  }, []);

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>
            <h1 className='text-2xl font-semibold'>{stepName}</h1>
          </div>
          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{ width: `${((step - 1) / totalSteps) * 100}%` }}
          />
        </div>

        <div className='w-full rounded-xl bg-white p-8 shadow-sm'>
          <div className='mb-8 rounded-xl border border-primary/20 bg-gamma p-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>
                Resume Title <span className='text-red-500'>*</span>
              </label>
              <span
                className={`text-xs ${
                  (resumeTitle?.length || 0) >= TITLE_MAX_CHAR_LIMIT
                    ? 'text-red-500'
                    : 'text-gray-400'
                }`}
              >
                {resumeTitle?.length || 0}/{TITLE_MAX_CHAR_LIMIT}
              </span>
            </div>
            <input
              name='title'
              maxLength={TITLE_MAX_CHAR_LIMIT}
              value={resumeTitle || ''}
              onChange={handleTitleChange}
              placeholder='e.g. Frontend Developer — 2026'
              className='mt-2 w-full rounded-lg border border-gray-300 p-3 focus:border-primary focus:outline-none'
            />
            <p className='mt-1 text-xs text-gray-500'>
              This will also be used as the filename when you download your
              resume as a PDF.
            </p>
          </div>

          <div className='grid grid-cols-3 gap-8'>
            {/* PHOTO */}
            <div className='flex flex-col items-center rounded-xl border p-6'>
              <div>
                <label htmlFor='fileInput'>
                  {resumePic ? (
                    <img
                      id='profilePicture'
                      src={resumePic}
                      alt='Profile'
                      className='h-28 w-28 cursor-pointer rounded-full border-2 border-gray-300 object-cover shadow-md'
                    />
                  ) : (
                    <div className='flex h-28 w-28 flex-col items-center justify-center gap-2 rounded-full border-2 border-dashed bg-gray-100 text-sm text-gray-400'>
                      <CameraIcon size={20} />
                      <p className='text-sm'>Upload File</p>
                    </div>
                  )}
                </label>
                <input
                  type='file'
                  id='fileInput'
                  onChange={handleUploadFile}
                  accept='image/png, image/jpeg'
                  className='hidden'
                />
              </div>

              <p className='mt-3 text-xs text-gray-400'>
                Profile Photo (Optional)
              </p>
              <p className='mt-1 text-center text-xs text-gray-400'>
                Please upload a professional passport-style photo
              </p>
              {uploadError && (
                <p className='mt-1 text-center text-xs text-red-500'>
                  {uploadError}
                </p>
              )}
            </div>

            {/* FORM */}
            <div className='col-span-2 space-y-6'>
              <div className='grid grid-cols-2 gap-6'>
                <div>
                  <div className='flex items-center justify-between'>
                    <label className='text-sm font-medium'>
                      First Name <span className='text-red-500'>*</span>{' '}
                    </label>
                    <CharCount
                      value={personalInfo.firstName}
                      max={NAME_CHAR_LIMIT}
                    />
                  </div>
                  <input
                    name='firstName'
                    maxLength={NAME_CHAR_LIMIT}
                    value={personalInfo.firstName || ''}
                    onChange={handleChange}
                    className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
                  />
                  <p className='mb-1 mt-1 text-xs text-gray-400'>
                    As per official records
                  </p>
                </div>

                <div>
                  <div className='flex items-center justify-between'>
                    <label className='text-sm font-medium'>
                      Last Name <span className='text-red-500'>*</span>{' '}
                    </label>
                    <CharCount
                      value={personalInfo.lastName}
                      max={NAME_CHAR_LIMIT}
                    />
                  </div>
                  <input
                    name='lastName'
                    maxLength={NAME_CHAR_LIMIT}
                    value={personalInfo.lastName || ''}
                    onChange={handleChange}
                    className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
                  />
                </div>
              </div>

              <div>
                <div className='flex items-center justify-between'>
                  <label className='text-sm font-medium'>
                    Additional Name (Optional)
                  </label>
                  <CharCount
                    value={personalInfo.additionalName}
                    max={NAME_CHAR_LIMIT}
                  />
                </div>
                <input
                  name='additionalName'
                  maxLength={NAME_CHAR_LIMIT}
                  value={personalInfo.additionalName || ''}
                  onChange={handleChange}
                  className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
                />
              </div>

              <div className='rounded-lg bg-gamma p-4'>
                <p className='text-xs text-gray-500'>AUTO-DERIVED FULL NAME</p>
                <p className='mt-1 font-medium text-brand'>
                  {`${personalInfo.firstName} ${personalInfo.lastName}`.trim()}
                </p>
              </div>
            </div>
          </div>

          <div className='mt-6 grid grid-cols-1 gap-4 md:grid-cols-3'>
            <div className='rounded-xl bg-white p-5 shadow-sm'>
              <label className='text-sm font-medium'>
                Date of Birth (Optional)
              </label>
              <input
                type='date'
                name='dateOfBirth'
                value={personalInfo.dateOfBirth?.split('T')[0] || ''}
                onChange={handleChange}
                className='mt-2 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div className='rounded-xl bg-white p-5 shadow-sm'>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>
                  Nationality (Optional)
                </label>
                <CharCount value={personalInfo.nationality} />
              </div>
              <input
                name='nationality'
                placeholder='Enter nationality'
                maxLength={MAX_CHAR_LIMIT}
                value={personalInfo.nationality || ''}
                onChange={handleChange}
                className='mt-2 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div className='rounded-xl bg-white p-5 shadow-sm'>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>
                  Place of Birth (Optional)
                </label>
                <CharCount value={personalInfo.placeOfBirth} />
              </div>
              <input
                name='placeOfBirth'
                placeholder='Enter birthplace'
                maxLength={MAX_CHAR_LIMIT}
                value={personalInfo.placeOfBirth || ''}
                onChange={handleChange}
                className='mt-2 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>
          </div>

          <div className='mt-10 flex items-center justify-end'>
            <div className='flex items-center gap-6'>
              <Button
                label='Continue'
                handleClickBtn={onNext}
                variant='contained'
                disabled={!isFormValid}
                iconPosition='right'
              >
                <ArrowRight size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoForm;
